import { FC } from 'react'
import { motion } from 'framer-motion'
import { FaMapMarkerAlt, FaClock, FaPhoneAlt } from 'react-icons/fa'
import { FaFacebookF } from 'react-icons/fa6'

interface ContactItem {
  icon: React.ReactNode
  title: string
  content: React.ReactNode
}

const contactItems: ContactItem[] = [
  {
    icon: <FaMapMarkerAlt className="text-xl" />,
    title: 'Address',
    content: (
      <a
        href="https://www.google.com/maps/search/?api=1&query=34.910986258834214,33.62032849547252"
        target="_blank"
        rel="noopener noreferrer"
        className="text-text-gray hover:text-primary transition-colors border-b border-dotted border-text-gray hover:border-primary"
      >
        149 Leoforos Faneromenis,
        <br />
        Larnaca, Cyprus, 6031
      </a>
    ),
  },
  {
    icon: <FaClock className="text-xl" />,
    title: 'Opening Hours',
    content: <span className="text-text-gray">Mon - Fri: 8:00 AM – 7:00 PM</span>,
  },
  {
    icon: <FaPhoneAlt className="text-xl" />,
    title: 'Phones',
    content: (
      <div className="space-y-1">
        {['+357 24 252225', '+357 99 977022', '+357 96 759387'].map((phone) => (
          <a
            key={phone}
            href={`tel:${phone.replace(/\s/g, '')}`}
            className="block text-text-gray hover:text-primary transition-colors font-medium"
          >
            {phone}
          </a>
        ))}
      </div>
    ),
  },
  {
    icon: <FaFacebookF className="text-xl" />,
    title: 'Facebook',
    content: (
      <a
        href="https://www.facebook.com/StudioFiguraLarnaca"
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:text-primary/80 transition-colors font-medium"
      >
        Follow us on Facebook
      </a>
    ),
  },
]

const Contact: FC = () => {
  return (
    <section id="contact" className="bg-white">
      <div className="grid lg:grid-cols-2">
        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="py-16 lg:py-24 px-4 sm:px-6 lg:px-16 xl:px-24 flex flex-col justify-center"
        >
          <span className="section-tag">Get in Touch</span>
          <h2 className="section-heading">Visit Us</h2>

          <div className="space-y-8 mt-8">
            {contactItems.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex gap-5"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary flex-shrink-0">
                  {item.icon}
                </div>
                
                <div>
                  <h3 className="font-bold text-text-dark text-lg mb-1">
                    {item.title}
                  </h3>
                  {item.content}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative min-h-[400px] lg:min-h-full"
        >
          <iframe
            src="https://maps.google.com/maps?q=34.910986258834214,33.62032849547252&hl=en&z=17&output=embed"
            className="absolute inset-0 w-full h-full border-0 grayscale contrast-75"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Studio Figura Larnaca Location"
          />
        </motion.div>
      </div>
    </section>
  )
}

export default Contact
